import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReviewHighlight } from './adapter/types';
import { getBlockLabel } from './utils';

interface BlockHighlightOverlayProps {
  hoveredBlock: ReviewHighlight | null;
  activeEditBlock: ReviewHighlight | null;
  pageNumber: number;
  /**
   * Transient "navigated-to" block (e.g. after clicking an outline item). When
   * it targets this page, a one-shot pulse animation plays to draw the eye.
   */
  flashBlock?: ReviewHighlight | null;
  /** Bumped on every navigation so re-clicking the same block replays the pulse. */
  flashNonce?: number;
}

type Rect = { x: number; y: number; width: number; height: number };

/**
 * A one-shot "radar ping" + tint that fires when the user navigates to a block
 * (from the outline). Purely visual; uses the Web Animations API so it leaves
 * no residual inline styles once it finishes (the persistent active-edit
 * highlight underneath stays intact).
 */
function FlashHighlight({ rect, nonce }: { rect: Rect; nonce: number }) {
  const ringRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ring = ringRef.current;
    const fill = fillRef.current;
    if (!ring) return;

    const ringAnim = ring.animate(
      [
        { opacity: 0.85, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(1.14)' },
      ],
      { duration: 1000, iterations: 2, easing: 'ease-out' }
    );
    const fillAnim = fill?.animate([{ opacity: 0.26 }, { opacity: 0 }], {
      duration: 1400,
      easing: 'ease-out',
    });

    return () => {
      ringAnim.cancel();
      fillAnim?.cancel();
    };
  }, [nonce]);

  return (
    <div
      aria-hidden
      className="absolute z-30 pointer-events-none"
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
    >
      <div
        ref={fillRef}
        className="absolute inset-0 rounded-md"
        style={{ backgroundColor: 'hsl(var(--selise-blue))', opacity: 0 }}
      />
      <div
        ref={ringRef}
        className="absolute -inset-1 rounded-lg"
        style={{ border: '2.5px solid hsl(var(--selise-blue))', opacity: 0 }}
      />
    </div>
  );
}

/**
 * Visual-only overlay that highlights the hovered or actively-editing block.
 * Uses pointer-events: none so mouse events pass through to the text layer
 * underneath — clicks are handled by the page container's onClick.
 *
 * Always mounted and opacity/transition driven, so moving between blocks
 * animates cleanly instead of flickering.
 */
export function BlockHighlightOverlay({
  hoveredBlock,
  activeEditBlock,
  pageNumber,
  flashBlock = null,
  flashNonce = 0,
}: BlockHighlightOverlayProps) {
  const { t } = useTranslation();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipBelow, setTooltipBelow] = useState(false);

  // Active editing state takes priority over hover
  const isActive = activeEditBlock !== null && activeEditBlock.pageNumber === pageNumber;
  const isHovered = !isActive && hoveredBlock !== null && hoveredBlock.pageNumber === pageNumber;
  const isVisible = isActive || isHovered;

  const highlight = isActive ? activeEditBlock : hoveredBlock;
  const rect = highlight?.rect ?? { x: 0, y: 0, width: 0, height: 0 };
  const block = highlight?.block;

  useEffect(() => {
    if (!isVisible) return;
    setTooltipBelow(rect.y < 48);
  }, [isVisible, rect.y]);

  const { label: blockLabel, Icon: BlockIcon } = getBlockLabel(t, block?.kind, block?.isTitle);

  return (
    <>
      {flashBlock && flashBlock.pageNumber === pageNumber && (
        <FlashHighlight rect={flashBlock.rect} nonce={flashNonce} />
      )}
      <div
        data-highlight-overlay
        data-active={isActive || undefined}
      aria-hidden
      className="block-highlight-overlay absolute z-20"
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        opacity: isVisible ? 1 : 0,
        pointerEvents: 'none',
        transition: 'opacity 150ms ease-out, left 120ms ease-out, top 120ms ease-out',
        backgroundColor: isActive
          ? 'hsl(var(--selise-blue) / 0.16)'
          : 'hsl(var(--selise-blue) / 0.10)',
        border: isActive
          ? '2px solid hsl(var(--selise-blue) / 0.7)'
          : '2px solid hsl(var(--selise-blue) / 0.45)',
        borderRadius: 6,
        boxShadow: isActive
          ? '0 2px 16px hsl(var(--selise-blue) / 0.22)'
          : isVisible
            ? '0 2px 12px hsl(var(--selise-blue) / 0.14)'
            : 'none',
      }}
    >
      {/* Tooltip — only shown on hover, not during active editing */}
      {!isActive && (
        <div
          ref={tooltipRef}
          className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[hsl(var(--selise-blue))] text-white text-xs font-semibold whitespace-nowrap shadow-lg pointer-events-none z-30 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            ...(tooltipBelow ? { top: 'calc(100% + 6px)' } : { bottom: 'calc(100% + 6px)' }),
            transition: 'opacity 150ms ease-out',
            maxWidth: 'min(250px, 90vw)',
          }}
        >
          <BlockIcon className="w-3 h-3 shrink-0" />
          <span>{t('GENERATEREVIEW_EDIT_TOOLTIP', { blockLabel })}</span>
        </div>
      )}
      </div>
    </>
  );
}
